import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { getTrialStatus } from '../services/api';
import MobileBottomNav from '../components/MobileBottomNav';
import { 
  Users, 
  Calendar, 
  ClipboardList, 
  Swords, 
  Video, 
  Settings, 
  LogOut,
  LayoutDashboard,
  ChevronLeft,
  ChevronRight,
  Crown,
  Menu,
  X,
  Scale,
  UserCheck,
  CalendarDays,
  AlertTriangle,
  Shield,
  GitCompare,
  FileText,
  Mail,
  HelpCircle,
  Gift,
  Timer,
  Share2,
  CreditCard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Performance Hub' },
  { path: '/fighters', icon: Users, label: 'Fight Team', priority: true },
  { path: '/training-camps', icon: Calendar, label: 'Fight Camps', priority: true },
  { path: '/assessments', icon: ClipboardList, label: 'Assessments', priority: true },
  { path: '/fights', icon: Swords, label: 'Events' },
  { path: '/countdown', icon: Timer, label: 'Fight Countdown' },
  { path: '/weight-tracking', icon: Scale, label: 'Weight Tracking' },
  { path: '/compare', icon: GitCompare, label: 'Compare' },
  { path: '/calendar', icon: CalendarDays, label: 'Camp Calendar' },
  { path: '/attendance', icon: UserCheck, label: 'Attendance' },
  { path: '/membership-plans', icon: CreditCard, label: 'Memberships', adminOnly: true },
  { path: '/videos', icon: Video, label: 'Videos', goldOnly: true },
  { path: '/reports', icon: FileText, label: 'Reports' },
  { path: '/notifications', icon: Mail, label: 'Notifications', adminOnly: true },
  { path: '/social', icon: Share2, label: 'Social Sharing' },
  { path: '/referrals', icon: Gift, label: 'Referrals' },
  { path: '/help', icon: HelpCircle, label: 'Help Center' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

export const DashboardLayout = ({ children }) => {
  const { user, gym, logout, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);

  // Redirect superadmin to admin panel
  useEffect(() => {
    if (isSuperAdmin && location.pathname === '/dashboard') {
      navigate('/admin');
    }
  }, [isSuperAdmin, location.pathname, navigate]);

  useEffect(() => {
    loadTrialStatus();
  }, []);

  const loadTrialStatus = async () => {
    try {
      const response = await getTrialStatus();
      setTrialStatus(response.data);
    } catch (error) {
      console.error('Failed to load trial status');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isGoldTier = gym && gym.subscription_tier === 'gold' && gym.subscription_status === 'active';
  const isTrial = trialStatus?.status === 'trial';
  const isTrialExpired = trialStatus?.status === 'expired';

  const NavContent = () => (
    <>
      <div className="p-4 border-b border-red-600/20">
        <Link to="/dashboard" className="flex items-center gap-3 group">
          <img 
            src="https://customer-assets.emergentagent.com/job_mma-gym-portal/artifacts/nfeq9dhc_file_00000000262c71fd9b8a476a6aaafaf3.png"
            alt="Black Arrow MMA"
            className={cn(
              "transition-all",
              collapsed ? "h-10 w-10 object-contain" : "h-12 w-auto"
            )}
          />
        </Link>
      </div>

      {/* Trial Banner */}
      {!collapsed && (isTrial || isTrialExpired) && (
        <div className={cn(
          "mx-4 mt-4 p-3 rounded-sm text-sm border",
          isTrialExpired ? "bg-red-600/10 border-red-600/30" : "bg-yellow-600/10 border-yellow-600/30"
        )}>
          {isTrialExpired ? (
            <div className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-body font-medium">Trial expired</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-yellow-400">
              <Crown className="w-4 h-4" />
              <span className="font-body font-medium">{trialStatus?.days_remaining} days left</span>
            </div>
          )}
          <Link to="/subscription" className="text-xs text-zinc-400 hover:text-white mt-1 block font-body">
            Upgrade now →
          </Link>
        </div>
      )}

      <nav className="flex-1 p-4 space-y-1">
        {/* Super Admin Panel Link */}
        {isSuperAdmin && (
          <Link
            to="/admin"
            onClick={() => setMobileOpen(false)}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all mb-2",
              location.pathname === '/admin'
                ? "bg-purple-600/20 text-purple-500 border-l-2 border-purple-600" 
                : "text-purple-400 hover:text-purple-300 hover:bg-purple-900/20"
            )}
          >
            <Shield className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span>Admin Panel</span>}
          </Link>
        )}
        
        {navItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
          
          // Hide admin-only items from non-admins
          if (item.adminOnly && !isAdmin) {
            return null;
          }
          
          if (item.goldOnly && !isGoldTier) {
            return (
              <Link
                key={item.path}
                to="/subscription"
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all",
                  "text-zinc-500 hover:text-zinc-400 hover:bg-zinc-900"
                )}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1">{item.label}</span>
                    <Crown className="w-4 h-4 text-yellow-500" />
                  </>
                )}
              </Link>
            );
          }

          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all",
                isActive 
                  ? "bg-red-600/20 text-red-500 border-l-2 border-red-600" 
                  : item.priority 
                    ? "text-zinc-300 hover:text-white hover:bg-zinc-900"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-900"
              )}
            >
              <item.icon className={cn(
                "w-5 h-5 flex-shrink-0",
                item.priority && !isActive && "text-red-500/70"
              )} />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-zinc-800">
        {!collapsed && (
          <div className="mb-4 p-3 bg-zinc-900 rounded-sm">
            <p className="text-sm text-zinc-400">{user?.name}</p>
            <p className="text-xs text-zinc-600">{user?.email}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className={cn(
                "text-xs px-2 py-0.5 rounded-sm uppercase font-medium",
                isAdmin ? "bg-red-600/20 text-red-500" : "bg-zinc-800 text-zinc-400"
              )}>
                {user?.role}
              </span>
              {gym && gym.subscription_tier !== 'none' && (
                <span className={cn(
                  "text-xs px-2 py-0.5 rounded-sm uppercase font-medium",
                  gym.subscription_tier === 'gold' 
                    ? "bg-yellow-500/20 text-yellow-500" 
                    : "bg-zinc-600/20 text-zinc-400"
                )}>
                  {gym.subscription_tier}
                </span>
              )}
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          onClick={handleLogout}
          className={cn(
            "w-full justify-start gap-3 text-zinc-400 hover:text-white hover:bg-zinc-900",
            collapsed && "justify-center"
          )}
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span>Logout</span>}
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-ba-black flex noise-overlay">
      {/* Desktop Sidebar */}
      <aside className={cn(
        "hidden md:flex flex-col bg-ba-black/80 backdrop-blur-xl border-r border-ba-border transition-all duration-300",
        collapsed ? "w-20" : "w-64"
      )}>
        <NavContent />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute bottom-20 -right-3 w-6 h-6 bg-ba-surface border border-ba-border rounded-full flex items-center justify-center hover:bg-zinc-800 hover:border-red-600/50 transition-all"
          style={{ left: collapsed ? '64px' : '240px' }}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-ba-black/90 backdrop-blur-xl border-b border-red-600/20 px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_mma-gym-portal/artifacts/nfeq9dhc_file_00000000262c71fd9b8a476a6aaafaf3.png"
            alt="Black Arrow MMA"
            className="h-8 w-auto"
          />
        </Link>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 text-zinc-400 hover:text-white"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            />
            {/* Sidebar */}
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="md:hidden fixed inset-y-0 left-0 z-50 w-72 bg-ba-black pt-16 flex flex-col border-r border-ba-border"
            >
              <NavContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 overflow-auto relative">
        {/* Brand Watermark */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-heading text-[20vw] text-white/[0.015] pointer-events-none select-none whitespace-nowrap tracking-wider">
          BLACK ARROW
        </div>
        
        <div className="md:hidden h-16" /> {/* Mobile header spacer */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="p-4 sm:p-6 relative z-10 pb-24 md:pb-6"
        >
          {children}
        </motion.div>
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav onMenuClick={() => setMobileOpen(true)} />
    </div>
  );
};

export default DashboardLayout;
